import React from 'react';
import {
  Document as PDFDocument,
  Page,
  Text,
  View,
  StyleSheet
} from '@react-pdf/renderer';
import { Invoice, FilterSummary, formatCurrency, formatDate } from '../../config';

// =======================================
// PROPS
// =======================================

interface InvoiceExportPDFProps {
  invoices: Invoice[];
  filterSummary: FilterSummary;
  totals: {
    ourFee: number;
    disbursements: number;
    totalAmount: number;
  };
  generatedDate: string;
  invoiceCount: number;
}

// =======================================
// STYLES
// =======================================

const BORDER_COLOR = '#CCCCCC';
const HEADER_BG = '#2C3E50';
const HEADER_TEXT = '#FFFFFF';
const ROW_ALT_BG = '#F8F9FA';
const TOTALS_BG = '#EBF0F5';

const styles = StyleSheet.create({
  page: {
    paddingTop: 30,
    paddingBottom: 50,
    paddingLeft: 28,
    paddingRight: 28,
    fontSize: 8,
    fontFamily: 'Helvetica',
    flexDirection: 'column',
  },

  // Report header
  reportHeader: {
    marginBottom: 12,
    paddingBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: HEADER_BG,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 6,
  },
  title: {
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    color: HEADER_BG,
  },
  generatedDate: {
    fontSize: 8,
    color: '#666666',
  },
  filterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 4,
  },
  filterTag: {
    flexDirection: 'row',
    backgroundColor: '#F0F2F5',
    borderRadius: 3,
    paddingHorizontal: 5,
    paddingVertical: 2,
  },
  filterLabel: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: '#555555',
  },
  filterValue: {
    fontSize: 7,
    color: '#333333',
    marginLeft: 2,
  },
  recordCount: {
    fontSize: 7,
    color: '#666666',
    marginTop: 4,
  },

  // Table
  table: {
    width: '100%',
    marginTop: 4,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: HEADER_BG,
    minHeight: 22,
    alignItems: 'center',
  },
  tableRow: {
    flexDirection: 'row',
    minHeight: 18,
    alignItems: 'center',
    borderBottomWidth: 0.5,
    borderBottomColor: BORDER_COLOR,
  },
  tableRowAlt: {
    backgroundColor: ROW_ALT_BG,
  },

  // Column cells — header
  thCell: {
    paddingVertical: 4,
    paddingHorizontal: 4,
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: HEADER_TEXT,
  },
  // Column cells — body
  tdCell: {
    paddingVertical: 3,
    paddingHorizontal: 4,
    fontSize: 7.5,
    color: '#333333',
  },
  tdCellRight: {
    textAlign: 'right',
  },

  // Column widths (percentage of landscape A4 width)
  colInvNum:     { width: '7%' },
  colDate:       { width: '9%' },
  colJobNum:     { width: '6%' },
  colClient:     { width: '16%' },
  colDesc:       { width: '24%' },
  colOurFee:     { width: '10%' },
  colDisburse:   { width: '10%' },
  colTotal:      { width: '10%' },
  colStatus:     { width: '8%' },

  // Totals row
  totalsRow: {
    flexDirection: 'row',
    backgroundColor: TOTALS_BG,
    minHeight: 22,
    alignItems: 'center',
    borderTopWidth: 1.5,
    borderTopColor: HEADER_BG,
    borderBottomWidth: 1.5,
    borderBottomColor: HEADER_BG,
  },
  totalsLabel: {
    paddingHorizontal: 4,
    paddingVertical: 4,
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: HEADER_BG,
  },
  totalsValue: {
    paddingHorizontal: 4,
    paddingVertical: 4,
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: HEADER_BG,
    textAlign: 'right',
  },

  // Page footer
  pageFooter: {
    position: 'absolute',
    bottom: 20,
    left: 28,
    right: 28,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 0.5,
    borderTopColor: BORDER_COLOR,
    paddingTop: 6,
  },
  footerText: {
    fontSize: 7,
    color: '#999999',
  },
});

// =======================================
// SUB-COMPONENTS
// =======================================

const TableHeader: React.FC = () => (
  <View style={styles.tableHeaderRow} fixed>
    <Text style={[styles.thCell, styles.colInvNum]}>Inv #</Text>
    <Text style={[styles.thCell, styles.colDate]}>Date</Text>
    <Text style={[styles.thCell, styles.colJobNum]}>Job #</Text>
    <Text style={[styles.thCell, styles.colClient]}>Client</Text>
    <Text style={[styles.thCell, styles.colDesc]}>Description</Text>
    <Text style={[styles.thCell, styles.colOurFee, styles.tdCellRight]}>Our Fee</Text>
    <Text style={[styles.thCell, styles.colDisburse, styles.tdCellRight]}>Disburse</Text>
    <Text style={[styles.thCell, styles.colTotal, styles.tdCellRight]}>Total</Text>
    <Text style={[styles.thCell, styles.colStatus]}>Status</Text>
  </View>
);

const TableRow: React.FC<{ invoice: Invoice; index: number }> = ({ invoice, index }) => (
  <View style={[styles.tableRow, index % 2 === 1 ? styles.tableRowAlt : {}]} wrap={false}>
    <Text style={[styles.tdCell, styles.colInvNum]}>{invoice.invoice_number}</Text>
    <Text style={[styles.tdCell, styles.colDate]}>{formatDate(invoice.invoice_date)}</Text>
    <Text style={[styles.tdCell, styles.colJobNum]}>{invoice.job_number ?? '—'}</Text>
    <Text style={[styles.tdCell, styles.colClient]}>{invoice.client_name ?? '—'}</Text>
    <Text style={[styles.tdCell, styles.colDesc]}>{invoice.project_description ?? '—'}</Text>
    <Text style={[styles.tdCell, styles.colOurFee, styles.tdCellRight]}>{formatCurrency(invoice.our_fee)}</Text>
    <Text style={[styles.tdCell, styles.colDisburse, styles.tdCellRight]}>{formatCurrency(invoice.disbursements)}</Text>
    <Text style={[styles.tdCell, styles.colTotal, styles.tdCellRight]}>{formatCurrency(invoice.total_amount)}</Text>
    <Text style={[styles.tdCell, styles.colStatus]}>{invoice.status}</Text>
  </View>
);

const TotalsRow: React.FC<{ totals: InvoiceExportPDFProps['totals'] }> = ({ totals }) => {
  const labelPercent = 7 + 9 + 6 + 16 + 24;

  return (
    <View style={styles.totalsRow} wrap={false}>
      <Text style={[styles.totalsLabel, { width: `${labelPercent}%` }]}>TOTALS</Text>
      <Text style={[styles.totalsValue, styles.colOurFee]}>{formatCurrency(totals.ourFee)}</Text>
      <Text style={[styles.totalsValue, styles.colDisburse]}>{formatCurrency(totals.disbursements)}</Text>
      <Text style={[styles.totalsValue, styles.colTotal]}>{formatCurrency(totals.totalAmount)}</Text>
      <Text style={[styles.totalsValue, styles.colStatus]} />
    </View>
  );
};

// =======================================
// FILTER SUMMARY TAGS
// =======================================

interface FilterTagProps {
  label: string;
  value: string;
}

const FilterTag: React.FC<FilterTagProps> = ({ label, value }) => (
  <View style={styles.filterTag}>
    <Text style={styles.filterLabel}>{label}:</Text>
    <Text style={styles.filterValue}>{value}</Text>
  </View>
);

// =======================================
// MAIN COMPONENT
// =======================================

const InvoiceExportPDF: React.FC<InvoiceExportPDFProps> = ({
  invoices,
  filterSummary,
  totals,
  generatedDate,
  invoiceCount,
}) => {
  const filterTags: { label: string; value: string }[] = [];

  if (filterSummary.search) {
    filterTags.push({ label: 'Search', value: `"${filterSummary.search}"` });
  }
  filterTags.push({ label: 'Status', value: filterSummary.status });
  filterTags.push({ label: 'Date', value: filterSummary.dateRange });
  if (filterSummary.client !== 'All') {
    filterTags.push({ label: 'Client', value: filterSummary.client });
  }
  if (filterSummary.jobTypes !== 'All') {
    filterTags.push({ label: 'Types', value: filterSummary.jobTypes });
  }
  if (filterSummary.projectStatus !== 'All') {
    filterTags.push({ label: 'Project Status', value: filterSummary.projectStatus });
  }
  if (filterSummary.invoiceRange) {
    filterTags.push({ label: 'Inv #', value: filterSummary.invoiceRange });
  }

  return (
    <PDFDocument>
      <Page size="A4" orientation="landscape" style={styles.page} wrap>
        {/* Report Header */}
        <View style={styles.reportHeader} fixed>
          <View style={styles.titleRow}>
            <Text style={styles.title}>Invoice Register</Text>
            <Text style={styles.generatedDate}>Generated {generatedDate}</Text>
          </View>
          <View style={styles.filterRow}>
            {filterTags.map((tag, i) => (
              <FilterTag key={i} label={tag.label} value={tag.value} />
            ))}
          </View>
          <Text style={styles.recordCount}>{invoiceCount.toLocaleString()} invoice{invoiceCount !== 1 ? 's' : ''}</Text>
        </View>

        {/* Table */}
        <View style={styles.table}>
          <TableHeader />
          {invoices.map((invoice, index) => (
            <TableRow key={invoice.id} invoice={invoice} index={index} />
          ))}
          <TotalsRow totals={totals} />
        </View>

        {/* Page Footer */}
        <View style={styles.pageFooter} fixed>
          <Text style={styles.footerText}>TerpOps — Invoice Register Export</Text>
          <Text style={styles.footerText} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>
      </Page>
    </PDFDocument>
  );
};

export default InvoiceExportPDF;
